import React, { useMemo, useRef, useEffect, useCallback } from "react";
import { useGLTF } from "@react-three/drei";
import { useFrame, useThree } from "@react-three/fiber";
import * as THREE from "three";
import { MovementRegistry, resetStore } from "../movements/MovementRegistry";

// ── Single GLTF Part ──────────────────────────────────────────
function SingleModel({ url, onPartsReady, metalness, roughness, ropeMetalness, ropeRoughness }) {
    const { scene, animations } = useGLTF(url);
    const clonedScene = useMemo(() => {
        const clone = scene.clone(true);
        clone.traverse(child => {
            if (child.isMesh && child.material) child.material = child.material.clone();
        });
        return clone;
    }, [scene]);
    useEffect(() => {
        clonedScene.traverse(child => {
            if (child.isMesh && child.material && (child.material.isMeshStandardMaterial || child.material.isMeshPhysicalMaterial)) {
                const isRope = child.parent?.name === "rope" || child.name === "rope";
                child.material.metalness = isRope ? ropeMetalness : metalness;
                child.material.roughness = isRope ? ropeRoughness : roughness;
            }
        });
    }, [clonedScene, metalness, roughness, ropeMetalness, ropeRoughness]);
    useEffect(() => {
        const parts = {};
        clonedScene.traverse(child => { if (child.name) parts[child.name] = child; });
        onPartsReady(parts, animations, clonedScene);
    }, [clonedScene, animations, onPartsReady]);
    return <primitive object={clonedScene} dispose={null} />;
}

// ── Controller Logic ──────────────────────────────────────────
function MeshController({
    groupRef, autoRotate, rotationAxis, baseScaleRef, sliderRotRef,
    resetSignal, scaleTargetRef, isReady, isMobile, paused, sliderMoveTick
}) {
    const { gl } = useThree();
    const drag = useRef({ active: false, lastX: 0, lastY: 0, velX: 0, velY: 0 });
    const pinch = useRef({ active: false, lastDist: 0 });
    const scrollAccum = useRef(0);
    const isInteracting = useRef(false);
    const isSyncingToSlider = useRef(false);
    useEffect(() => {
        isSyncingToSlider.current = true;
    }, [sliderMoveTick]);
    useEffect(() => {
        if (!groupRef.current) return;
        groupRef.current.quaternion.setFromEuler(new THREE.Euler(...sliderRotRef.current));
        scaleTargetRef.current = baseScaleRef.current;
        groupRef.current.scale.setScalar(baseScaleRef.current);
        scrollAccum.current = 0; drag.current.velX = 0; drag.current.velY = 0;
    }, [resetSignal, baseScaleRef, groupRef, scaleTargetRef, sliderRotRef]);
    useEffect(() => {
        const canvas = gl.domElement;
        const getXY = (e) => e.touches ? { x: e.touches[0].clientX, y: e.touches[0].clientY } : { x: e.clientX, y: e.clientY };
        const onDown = (e) => {
            if (e.touches && e.touches.length === 2) return;
            const { x, y } = getXY(e);
            drag.current = { active: true, lastX: x, lastY: y, velX: 0, velY: 0 };
            isInteracting.current = true;
            isSyncingToSlider.current = false;
        };
        const onMove = (e) => {
            if (!drag.current.active || !groupRef.current || (e.touches && e.touches.length === 2)) return;
            const { x, y } = getXY(e);
            const dx = x - drag.current.lastX; const dy = y - drag.current.lastY;
            drag.current.velX = dx; drag.current.velY = dy; drag.current.lastX = x; drag.current.lastY = y;
            groupRef.current.quaternion.premultiply(new THREE.Quaternion().setFromAxisAngle(new THREE.Vector3(0, 1, 0), dx * 0.008));
            groupRef.current.quaternion.premultiply(new THREE.Quaternion().setFromAxisAngle(new THREE.Vector3(1, 0, 0), dy * 0.008));
        };
        const onWheel = (e) => { e.preventDefault(); isInteracting.current = true; scrollAccum.current += e.deltaY * 0.15; };
        const onTouchStart = (e) => { if (e.touches.length === 2) { isInteracting.current = true; isSyncingToSlider.current = false; const dx = e.touches[0].clientX - e.touches[1].clientX; const dy = e.touches[0].clientY - e.touches[1].clientY; pinch.current = { active: true, lastDist: Math.sqrt(dx * dx + dy * dy) }; } };
        const onTouchMove = (e) => {
            if (!pinch.current.active || e.touches.length !== 2) return;
            const dx = e.touches[0].clientX - e.touches[1].clientX; const dy = e.touches[0].clientY - e.touches[1].clientY;
            const dist = Math.sqrt(dx * dx + dy * dy);
            if (pinch.current.lastDist > 0) scaleTargetRef.current *= (1 + (dist / pinch.current.lastDist - 1) * 0.8);
            pinch.current.lastDist = dist;
        };
        const onUp = () => { drag.current.active = false; pinch.current.active = false; isInteracting.current = false; };
        canvas.addEventListener("mousedown", onDown); canvas.addEventListener("mousemove", onMove);
        canvas.addEventListener("mouseup", onUp); canvas.addEventListener("mouseleave", onUp);
        canvas.addEventListener("touchstart", onDown, { passive: true }); canvas.addEventListener("touchmove", onMove, { passive: true });
        canvas.addEventListener("touchend", onUp); canvas.addEventListener("wheel", onWheel, { passive: false });
        canvas.addEventListener("touchstart", onTouchStart, { passive: true }); canvas.addEventListener("touchmove", onTouchMove, { passive: true });
        return () => {
            canvas.removeEventListener("mousedown", onDown); canvas.removeEventListener("mousemove", onMove);
            canvas.removeEventListener("mouseup", onUp); canvas.removeEventListener("mouseleave", onUp);
            canvas.removeEventListener("touchstart", onDown); canvas.removeEventListener("touchmove", onMove);
            canvas.removeEventListener("touchend", onUp); canvas.removeEventListener("wheel", onWheel);
            canvas.removeEventListener("touchstart", onTouchStart); canvas.removeEventListener("touchmove", onTouchMove);
        };
    }, [gl, groupRef, scaleTargetRef]);
    useFrame((_, delta) => {
        if (!groupRef.current || !isReady.current) return;
        if (drag.current.active) {
            // Dragging — no auto-rotate
        } else if (isSyncingToSlider.current) {
            const targetQuat = new THREE.Quaternion().setFromEuler(new THREE.Euler(...sliderRotRef.current));
            groupRef.current.quaternion.slerp(targetQuat, 0.1);
            if (groupRef.current.quaternion.angleTo(targetQuat) < 0.001) isSyncingToSlider.current = false;
        } else if (autoRotate && !paused) {
            const axisMap = { x: new THREE.Vector3(1, 0, 0), y: new THREE.Vector3(0, 1, 0), z: new THREE.Vector3(0, 0, 1) };
            groupRef.current.quaternion.multiply(new THREE.Quaternion().setFromAxisAngle(axisMap[rotationAxis ?? "y"], delta * 0.15));
        } else if (Math.abs(drag.current.velX) > 0.01 || Math.abs(drag.current.velY) > 0.01) {
            groupRef.current.quaternion.premultiply(new THREE.Quaternion().setFromAxisAngle(new THREE.Vector3(0, 1, 0), drag.current.velX * 0.008));
            groupRef.current.quaternion.premultiply(new THREE.Quaternion().setFromAxisAngle(new THREE.Vector3(1, 0, 0), drag.current.velY * 0.008));
            drag.current.velX *= 0.9; drag.current.velY *= 0.9;
        }
        if (Math.abs(scrollAccum.current) > 0.1) {
            scaleTargetRef.current *= (1 - scrollAccum.current * 0.001);
            scrollAccum.current *= 0.8;
            if (Math.abs(scrollAccum.current) < 0.1) scrollAccum.current = 0;
        }
        const base = baseScaleRef.current;
        if (base) {
            const isUserZooming = pinch.current.active || Math.abs(scrollAccum.current) > 0.05;
            const minRest = base * 0.85; const maxRest = isMobile ? base * 4.0 : base * 2.5;
            scaleTargetRef.current = THREE.MathUtils.clamp(scaleTargetRef.current, base * 0.2, base * 12.0);
            if (!isUserZooming) {
                if (scaleTargetRef.current < minRest) scaleTargetRef.current = THREE.MathUtils.lerp(scaleTargetRef.current, minRest, 0.1);
                else if (scaleTargetRef.current > maxRest) scaleTargetRef.current = THREE.MathUtils.lerp(scaleTargetRef.current, maxRest, 0.1);
            }
            const diff = scaleTargetRef.current - groupRef.current.scale.x;
            if (Math.abs(diff) > 0.0001) groupRef.current.scale.setScalar(groupRef.current.scale.x + diff * 0.1);
        }
    });
    return null;
}

// ── Root Component ────────────────────────────────────────────
export default function FiveOhSevenModel({
    id, urls, paused, initialZoomPct, rotation, autoRotate, rotationAxis,
    resetSignal, metalness, roughness, ropeMetalness, ropeRoughness, isMobile
}) {
    const groupRef = useRef();
    const innerRef = useRef();
    const baseScaleRef = useRef(null);
    const scaleTargetRef = useRef(1);
    const sliderRotRef = useRef([0, 0, 0]);
    const isReady = useRef(false);
    const hasInitialized = useRef(false);
    const fittedScaleRef = useRef(null);
    const mixerRef = useRef(null);
    const [sliderMoveTick, setSliderMoveTick] = React.useState(0);

    const handlePartsReady = useCallback((parts, animations, rootScene) => {
        if (hasInitialized.current) return;
        groupRef.current.quaternion.set(0, 0, 0, 1);
        groupRef.current.scale.set(1, 1, 1);
        const box = new THREE.Box3().setFromObject(innerRef.current);
        if (!box.isEmpty()) {
            const center = new THREE.Vector3();
            box.getCenter(center);
            innerRef.current.position.set(-center.x, -center.y, -center.z);
            const size = new THREE.Vector3();
            box.getSize(size);
            fittedScaleRef.current = 4.5 / Math.max(size.x, size.y, size.z);
        }
        const s = fittedScaleRef.current * ((initialZoomPct ?? 100) / 100);
        baseScaleRef.current = s;
        scaleTargetRef.current = s;
        groupRef.current.scale.setScalar(s);
        sliderRotRef.current = rotation ?? [0, 0, 0];
        groupRef.current.quaternion.setFromEuler(new THREE.Euler(...sliderRotRef.current));
        isReady.current = true;
        hasInitialized.current = true;

        // ── GLTF animation mixer — plays any baked keyframe animations in the GLB
        if (animations && animations.length > 0) {
            console.log(`[${id}] found ${animations.length} animation(s):`, animations.map(a => a.name));
            const mixer = new THREE.AnimationMixer(rootScene);
            animations.forEach(clip => {
                const action = mixer.clipAction(clip);
                action.setLoop(THREE.LoopRepeat, Infinity);
                action.play();
            });
            mixerRef.current = mixer;
        } else {
            console.log(`[${id}] no GLTF animations found`);
            mixerRef.current = null;
        }
    }, [id, initialZoomPct, rotation]);

    useEffect(() => {
        resetStore(id);
    }, [id]);

    useEffect(() => {
        if (!isReady.current || !fittedScaleRef.current) return;
        const s = fittedScaleRef.current * ((initialZoomPct ?? 100) / 100);
        baseScaleRef.current = s;
        scaleTargetRef.current = s;
        sliderRotRef.current = [...rotation];
        setSliderMoveTick(t => t + 1);
    }, [rotation, initialZoomPct]);

    useFrame((state, delta) => {
        if (paused || !isReady.current) return;

        // Tick GLTF mixer if present
        if (mixerRef.current) mixerRef.current.update(delta);

        // Tick per-movement registry logic (pulleys, texture scroll, etc.)
        const logic = MovementRegistry[id];
        const parts = {};
        innerRef.current.traverse(child => { if (child.name) parts[child.name] = child; });
        if (logic) logic(parts, delta, state, mixerRef.current); // ← just add the fourth argument here
    });

    return (
        <group ref={groupRef}>
            <group ref={innerRef}>
                {urls.map((url) => (
                    <SingleModel key={url} url={url} onPartsReady={handlePartsReady} metalness={metalness} roughness={roughness} ropeMetalness={ropeMetalness} ropeRoughness={ropeRoughness} />
                ))}
            </group>
            <MeshController
                groupRef={groupRef} autoRotate={autoRotate} rotationAxis={rotationAxis}
                baseScaleRef={baseScaleRef} sliderRotRef={sliderRotRef}
                resetSignal={resetSignal} scaleTargetRef={scaleTargetRef}
                isReady={isReady} isMobile={isMobile} paused={paused}
                sliderMoveTick={sliderMoveTick}
            />
        </group>
    );
}